// §III seven-layers chart — ported VERBATIM from the R10 §III standalone `chart()` /
// `geom()` / `G` / `yVal` (Claude Design canon F3), NOT reconstructed. Every element CD
// draws is here: the glow filter + vitality underlay, the shaded "now" band, the
// per-stage column separators, the dashed zero baseline, the CD Catmull-Rom spline
// (curve.js), the year axis, the stage names on top, and the open-start fade.
//
// Columns are laid out by the wizard's stage DATES (`geom()` → xYear), exactly like
// CD, whenever `stages` carries them (step2 metadata; see datasets). Personas CD
// never authored dates for fall back to EVEN columns with roman-only labels and no
// year ticks — the same reduced state CD shows when a stage has no date.
//
// F3 deltas vs the earlier port, both driven by CD `chart()`:
//   • a layer value of `null` is a HOLE, not a zero: its point is dropped from the
//     path (CD's `.filter(v.sliders[i][key]!==null)`), so a layer empty on all N
//     stages draws NO curve (empty `d`) — it must not be a flat line at zero.
//   • stage-name fit is a per-CHART decision (CD `fitStageLabels`): if any name
//     overflows its column the whole row falls back to roman. That needs real font
//     metrics, so the fitted result is passed in as `labelMode` (the board declares
//     it, browser-verified); a client effect re-fits live. SSR renders the declared
//     mode — exactly the §II measured-pagination contract.

import { LAYERS } from './layersConfig.js';
import { smoothPath } from './curve.js';
import { READING_YEAR } from '../contracts.js';
// Список I…XV переехал в общий `src/roman.js` (B5-1): шаг 3 печатает те же номера в стене
// финала, и вторая копия одного списка разъехалась бы молча. Значения не менялись — равенство
// прежнему литералу проверяется в `src/roman.test.js`.
import { ROMAN } from '../roman.js';

// Geometry constants — verbatim from CD `G` (R9 numbers, r2a-geom.js).
const G = { W: 1000, H: 320, AXIS_LEFT: 60, AXIS_RIGHT: 988, Y_TOP: 28, Y_BOT: 264 };
G.Y_MID = (G.Y_TOP + G.Y_BOT) / 2;
const yVal = (v) => G.Y_MID - (v / 3) * ((G.Y_BOT - G.Y_TOP) / 2);
const yy = (y) => `'${String(Math.floor(y)).slice(-2)}`;

// CD `geom()` — year-driven columns + tick years. Verbatim.
function geomFromDates(stages) {
  const N = stages.length;
  // ── ДОМЕН НАКРЫВАЕТ ДАННЫЕ, А НЕ КРАЙНИЕ ЭЛЕМЕНТЫ МАССИВА (QA cc-33) ───────────────────────
  //
  // CD пишет домен как `[stages[0].start, stages[N-1].end]` — верно ровно до тех пор, пока даты
  // хронологичны. Хронология же у нас СОВЕЩАТЕЛЬНАЯ: `wizardInputWarnings` про порядок этапов
  // предупреждает, `validateWizardInput` пропускает молча, а импорт биографий и API идут мимо
  // визарда. На таком вводе домен переставал накрывать данные, и `span` мог стать ОТРИЦАТЕЛЬНЫМ —
  // тогда `Math.max(last - first, 1)` спасал от деления на ноль, но давал масштаб «928px на год».
  //
  // Замер 21.08, вход `a 2005→2010, b 2000→2003` (validateWizardInput чист): span = max(−2, 1) = 1,
  // подписи этапов печатались на **x = 2380 и x = −3188** при ширине борда 1000, ширина колонки
  // 4640. Все семь кривых считаются от тех же `xMid` — то есть §III печатала ПУСТУЮ рамку чарта.
  // Второй вход, `00→05 · 05→10 · 00→02`: x = 1220 и 3540, та же пустая рамка.
  // Ни NaN, ни падения, ни маркера деградации: лист выглядел целым и не содержал графика.
  //
  // Это та же половина гарда, что дописана сегодня в §IV `geom()`: там правая сторона (`last`
  // накрывает самую позднюю дату) была написана в cc-31, левая — нет. Здесь не было ни одной.
  // Канон не двигается: у всех бордов и датасетов крайние элементы И ЕСТЬ min/max известных дат.
  // `openStart` → своего начала у этапа нет, его `startY` в домен не входит (борд §IV v08 несёт
  // `openStart` И проставленный `start`, и домен CD начинается с КОНЦА такого этапа).
  //
  // ── ПУСТОЙ КОНЕЦ БОЛЬШЕ НЕ РОНЯЕТ ОСЬ (решение J, борд CD `Life Map - Temporal Boards v1`) ──
  //
  // Раньше `dated` требовал конечный `endY` У КАЖДОГО этапа: один непроставленный конец уводил
  // ВЕСЬ чарт в индексную раскладку, и §III печатала лист без единого года, пока §IV на том же
  // паспорте печатала восемь подписей. Замер 22.08: одна страница утверждает, что дат нет,
  // следующая их печатает. CD выбрал третью сторону развилки (не «§IV уходит следом» и не «§III
  // оставляет пустой без тика»): **обе печатают годы, пустой `end` не роняет ось нигде.**
  //
  // Механика взята у §IV дословно, а не придумана заново — в этом и смысл решения: домен строится
  // из ИЗВЕСТНЫХ дат, а конец колонки ищется по цепочке «свой конец → начало следующего этапа →
  // индексная позиция». Год не выдумывается ни в одной ветке: колонка без конца просто встаёт на
  // индексную границу, а ось продолжает жить на тех датах, которые человек проставил.
  const known = stages.flatMap((s) => [s.openStart ? null : s.startY, s.endY]).filter(Number.isFinite);
  const first0 = stages[0].openStart ? stages[0].endY : stages[0].startY;
  const lastEnd = stages[N - 1].endY;
  const first = Number.isFinite(first0) && known.length ? Math.min(first0, ...known) : first0;
  const last = known.length
    ? Math.max(Number.isFinite(lastEnd) ? lastEnd : READING_YEAR, ...known)
    : lastEnd;
  // Домена нет вовсе (дат не проставлено ни одной) → индексная раскладка, как было.
  const dated = Number.isFinite(first) && Number.isFinite(last);
  const span = Math.max(last - first, 1);
  const xIdx = (i) => G.AXIS_LEFT + (N <= 1 ? 0 : (i / N) * (G.AXIS_RIGHT - G.AXIS_LEFT));
  const xYear = (y) => (Number.isFinite(y) && dated
    ? G.AXIS_LEFT + ((y - first) / span) * (G.AXIS_RIGHT - G.AXIS_LEFT)
    : null);
  const cols = stages.map((s, i) => {
    const nextStart = i + 1 < N ? stages[i + 1].startY : null;
    const xStart = s.openStart ? 0 : (xYear(s.startY) ?? xIdx(i));
    const xEnd = xYear(s.endY) ?? xYear(nextStart) ?? xIdx(i + 1);
    return { xStart, xEnd, xMid: (xStart + xEnd) / 2, current: !!s.current, openStart: !!s.openStart, roman: ROMAN[i] ?? String(i + 1), name: s.name };
  });
  const yStart = Math.floor(first), yEnd = Math.floor(last);
  const ticks = [];
  for (let y = yStart; y < yEnd; y += 4) ticks.push(y);
  if (ticks.length && ticks[ticks.length - 1] < yEnd - 2) ticks.push(yEnd - 2);
  return { cols, first, last, xYear, ticks, dated };
}

// ── `geomEven` СНЕСЕНА 24.08. Ветка описывала вход, которого не существует ──
//
// Была: «fallback for personas without authored dates» — ровные колонки, римские подписи, ноль
// тиков. Замер (леджер `SYNC.md` строка 39): в неё уходили НОЛЬ из 29 биографий корпуса, ноль из
// 16 бордов §III, ноль датасетов, несущих `stages`. Из визарда вход недостижим — черновик этапа
// не закрывается без имени и даты начала (`s2 Stage Gate v5`). Второй мыслимый вход, паспорт
// без `stages` вовсе, до неё не доходил: `SectionIII` падает раньше, на `stages[0].openStart`.
//
// ЦЕНА, КОТОРУЮ ОНА УСПЕЛА ВЗЯТЬ. Функция утверждала своим существованием и своим комментарием,
// что бездатный вход бывает. По этому утверждению CD построил борд бездатной оси, а до того —
// разбор «§III печатает now, §IV печатает '26». Оба артефакта описывали состояние, до которого
// нельзя дойти. Мёртвая ветка здесь стоила не байтов, а двух чужих заходов.
//
// Ничего нового она не роняла: вход, на котором она срабатывала, уже падал строкой выше по стеку.
// Снос меняет не поведение, а сообщение — теперь оно называет причину и адрес.

export default function SevenLayersChart({ sliders, stages = null, gid = 'iii', labelMode = 'full' }) {
  const N = sliders.length;
  // `typeof x === 'number'` пропускает NaN — и тогда весь чарт строился на NaN-координатах.
  // Гейт проверяет, из чего строить ось. РАНЬШЕ здесь стояло `every(s => Number.isFinite(s.endY))`
  // — один пустой конец уводил в ровную раскладку весь лист (см. блок «пустой конец больше не
  // роняет ось» в `geomFromDates`). Теперь решает домен: есть из чего его построить — ось живёт.
  //
  // Ветки «не из чего» больше нет (`geomEven` снесена, см. выше): вход, которому она отвечала,
  // продукт не производит. Гейт не молчит и не рисует ровные колонки — он называет вход.
  const geo = Array.isArray(stages) && stages.length === N && stages.every((s) => s && typeof s === 'object')
    ? geomFromDates(stages) : null;
  if (!geo || !geo.dated) {
    throw new Error(
      `SevenLayersChart: ось строить не из чего — ${!geo
        ? `stages не пришли или не совпали со слайдерами (stages: ${Array.isArray(stages) ? stages.length : typeof stages}, sliders: ${N})`
        : 'ни одной финитной даты во всех этапах'}. Такой вход визард не производит; если он пришёл — это не-визардный путь (импорт, фикстура движка, ручной паспорт), и рисовать по нему нельзя. SYNC.md строка 39.`,
    );
  }
  const { cols, ticks, xYear } = geo;
  const lastCol = cols[N - 1];
  const anyCurrent = cols.some((c) => c.current);

  // one smoothed path per layer — CD `path(key)`: null is a HOLE (point dropped), so a
  // layer empty on every stage yields an empty `d` and draws nothing.
  const pathFor = (key) => {
    const pts = sliders
      .map((s, i) => {
        const raw = s[key];
        const missing = raw === null || raw === undefined;
        return { x: cols[i].xMid, y: yVal(missing ? 0 : Number(raw)), missing };
      })
      .filter((p) => !p.missing)
      .map((p) => [p.x, p.y]);
    return smoothPath(pts);
  };

  return (
    <svg
      className="lp-chart"
      data-field="chart"
      viewBox={`0 0 ${G.W} ${G.H + 28}`}
      preserveAspectRatio="none"
      style={{ height: 'var(--chart-h-layers)' }}
      role="img"
      aria-label="Seven layers across the recorded stages"
    >
      <defs>
        <linearGradient id={`pro-fade-${gid}`} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#0e0f12" stopOpacity="0" />
          <stop offset="100%" stopColor="#0e0f12" stopOpacity=".4" />
        </linearGradient>
        <filter id={`lp-glow-${gid}`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* open-start fade + boundary (first stage has no recorded beginning) */}
      {cols[0].openStart && (
        <>
          <rect x="0" y={G.Y_TOP - 8} width={G.AXIS_LEFT} height={G.Y_BOT - G.Y_TOP + 16} fill={`url(#pro-fade-${gid})`} />
          <line x1={G.AXIS_LEFT} y1={G.Y_TOP - 8} x2={G.AXIS_LEFT} y2={G.Y_BOT + 8} stroke="#3A3E48" strokeWidth=".75" strokeDasharray="2 4" />
        </>
      )}

      {/* dashed zero baseline — spans the full viewBox (CD) */}
      <line x1="0" y1={yVal(0)} x2={G.W} y2={yVal(0)} stroke="rgba(143,136,122,.22)" strokeDasharray="2 6" strokeWidth=".7" />

      {/* per-stage column separators, all but the last */}
      {cols.slice(0, -1).map((c, i) => (
        <line key={`sep-${i}`} x1={c.xEnd.toFixed(1)} y1={G.Y_TOP - 8} x2={c.xEnd.toFixed(1)} y2={G.Y_BOT + 8} stroke="#1C1E22" strokeWidth=".7" />
      ))}

      {/* shaded "now" band over the current (last) stage */}
      {lastCol.current && (
        <rect x={lastCol.xStart.toFixed(1)} y={G.Y_TOP - 8} width={(lastCol.xEnd - lastCol.xStart).toFixed(1)} height={G.Y_BOT - G.Y_TOP + 16} fill="rgba(212,138,60,.06)" />
      )}

      {/* glow underlay — layers flagged glow (vitality): wide, faint, blurred */}
      {LAYERS.map((L) =>
        L.glow ? (
          <path key={`glow-${L.key}`} d={pathFor(L.key)} fill="none" stroke={L.color} strokeWidth={(L.w * 2.2).toFixed(1)} strokeLinecap="round" strokeLinejoin="round" opacity=".22" filter={`url(#lp-glow-${gid})`} />
        ) : null
      )}

      {/* main layer lines */}
      {LAYERS.map((L) => (
        <path key={L.key} data-layer={L.id} d={pathFor(L.key)} fill="none" stroke={L.color} strokeWidth={L.w} strokeDasharray={L.dash ?? undefined} strokeLinecap="round" strokeLinejoin="round" />
      ))}

      {/* year axis — real ticks when dates exist (CD geom.ticks); else only "now".
          cc-30: the no-current branch reads the end from the STAGE, not the column —
          `geomFromDates` builds cols as {xStart,xEnd,xMid,current,openStart,roman,name} with no
          endY, so the old `cols[N-1].endY ?? 0` always fell through and printed `'0`. That branch
          only runs when `dated`, which requires a numeric endY on every stage. */}
      {/* `data-placeholder` стоял здесь под ветку без дат (`dated ? undefined : 'true'`) и на
          каждом живом входе разрешался в `undefined`, то есть атрибут не печатался НИ РАЗУ.
          Ветка снесена вместе с `geomEven` — атрибут ушёл за ней, байты не двинулись. */}
      <g data-field="chart_year_axis" style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '10px', letterSpacing: '.18em' }}>
        {ticks.map((y) => (
          <text key={`tick-${y}`} x={xYear(y).toFixed(1)} y={G.Y_BOT + 22} textAnchor="middle" fill="#8F887A">{yy(y)}</text>
        ))}
        {anyCurrent ? (
          <text x={(G.AXIS_RIGHT - 4).toFixed(1)} y={G.Y_BOT + 22} textAnchor="end" fill="#C7BFAE">now</text>
        ) : (
          // ПРАВЫЙ КРАЙ ПОДПИСЫВАЕТСЯ КРАЕМ ДОМЕНА, А НЕ КОНЦОМ ПОСЛЕДНЕГО ЭЛЕМЕНТА (пункт I).
          // Подпись стоит в крайней правой точке оси, а брала год у `stages[N-1].endY` — на
          // нехронологичном вводе это разные годы, и на `reverse-pair` в точке, означающей 2010,
          // печаталось '03. Борд CD `Temporal Boards v1` рисует ось по датам и помечает такую
          // подпись как спорящую с собственной осью. Плюс без этого пустой конец печатал бы
          // `yy(null)`: домен теперь живёт и там, где у последнего элемента конца нет.
          <text x={(G.AXIS_RIGHT - 4).toFixed(1)} y={G.Y_BOT + 22} textAnchor="end" fill="#8F887A">{yy(geo.last)}</text>
        )}
      </g>

      {/* stage names on top — "roman · name", full or roman per the fitted labelMode.
          data-* attrs mirror CD (label-mode/roman/full/col-w) so a client re-fit can
          flip the whole row to roman exactly like CD's fitStageLabels. */}
      {cols.map((c, i) => {
        const full = c.name ? `${c.roman} · ${c.name}` : c.roman;
        const mode = c.name ? labelMode : 'roman';
        return (
          <text
            key={`name-${i}`}
            x={c.xMid.toFixed(1)}
            y={G.Y_TOP - 14}
            textAnchor="middle"
            className={`lp-chart-stagename${c.current ? ' lp-chart-stagename-active' : ''}`}
            data-label-mode={mode}
            data-roman={c.roman}
            data-full={full}
            data-col-w={(c.xEnd - c.xStart).toFixed(1)}
            {...(c.name ? {} : { 'data-placeholder': 'true' })}
          >
            {mode === 'roman' ? c.roman : full}
          </text>
        );
      })}
    </svg>
  );
}
